import { faChartSimple } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { alpha, Box, Typography } from "@mui/material"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import { getCompactFormattedNumber } from "app/tools/number"
import { INVENTORY_MAP_VIEW_TYPES, MAP_INVENTORY_SCREENS_OPACITY } from "form/inventory/model"
import { getImpressionsStepBackgroundColors, getInventory } from "form/inventory/tools/common"
import React from "react"
import { connect } from "react-redux"
import { FlexBox } from "ui/components/FlexBox"

interface IImpressionLegendProps {
  viewType: INVENTORY_MAP_VIEW_TYPES
  mapImpressionsQuantiles: number[]
}

interface IImpressionQuantileProps {
  impressionCount: number
  backgroundColor: string
  isFirst?: boolean
  isLast?: boolean
  isUnique?: boolean
}

const ImpressionQuantile = React.memo(
  ({ impressionCount, backgroundColor, isLast, isFirst, isUnique }: IImpressionQuantileProps) => {
    const text = `${getCompactFormattedNumber(impressionCount)}${isLast ? " +" : isFirst ? "-" : ""}`
    return (
      <FlexBox ai="start" jc="start" sx={{ position: "relative", height: "1rem" }}>
        <Box
          sx={{
            width: ".5rem",
            height: "1rem",
            backgroundColor: alpha(backgroundColor, MAP_INVENTORY_SCREENS_OPACITY),
            mr: ".2rem"
          }}></Box>
        {impressionCount > 0 ? (
          <Typography
            color="textPrimary"
            fontSize=".7rem"
            fontWeight={500}
            sx={{
              position: isUnique ? "relative" : "absolute",
              top: isUnique ? undefined : "-.5rem",
              left: isUnique ? undefined : ".7rem",
              whiteSpace: "nowrap"
            }}>
            {text}
          </Typography>
        ) : null}
        <Typography
          // don't remove ! needed to display correct background color as the previous typo is absolute !
          color="textPrimary"
          fontSize=".7rem"
          sx={{ visibility: "hidden" }}>
          {text}
        </Typography>
      </FlexBox>
    )
  }
)

const ImpressionLegendFn = React.memo(({ viewType, mapImpressionsQuantiles }: IImpressionLegendProps) => {
  if (
    viewType !== INVENTORY_MAP_VIEW_TYPES.SCREENS ||
    mapImpressionsQuantiles.length === 0 ||
    (mapImpressionsQuantiles.length === 1 && mapImpressionsQuantiles[0] === 0)
  ) {
    return null
  }
  const stepBackgroundColors = getImpressionsStepBackgroundColors(mapImpressionsQuantiles)
  return (
    <FlexBox
      vertical
      ai="start"
      jc="start"
      sx={{
        position: "absolute",
        top: "2rem",
        left: "1rem",
        p: ".1rem",
        zIndex: 1,
        borderRadius: ".5rem",
        backgroundColor: theme => alpha(theme.palette.background.paper, 0.6),
        userSelect: "none",
        pointerEvents: "none"
      }}>
      <FlexBox sx={{ "gap": ".2rem", "& .icon": { color: theme => theme.palette.text.primary } }}>
        <FontAwesomeIcon icon={faChartSimple} className="icon" />
        <Typography color="textPrimary" fontSize=".8rem" fontWeight={500}>
          {translate("label.impressions")}
        </Typography>
      </FlexBox>
      <FlexBox vertical ai="start" jc="start" sx={{ flexDirection: "column-reverse", pt: ".1rem" }}>
        {mapImpressionsQuantiles.length === 1 ? (
          <ImpressionQuantile
            impressionCount={mapImpressionsQuantiles[0]}
            backgroundColor={stepBackgroundColors[0]}
            isUnique={true}
          />
        ) : (
          <>
            {mapImpressionsQuantiles
              .map((q, index) => (
                <ImpressionQuantile
                  impressionCount={q}
                  backgroundColor={stepBackgroundColors[index]}
                  isLast={index === mapImpressionsQuantiles.length - 1}
                  isFirst={index === 0}
                  key={index}
                />
              ))
              .filter((_q, index) => index === 0 || index === mapImpressionsQuantiles.length - 1 || index % 2 === 0)}
            <ImpressionQuantile
              impressionCount={-1}
              backgroundColor={stepBackgroundColors[stepBackgroundColors.length - 1]}
            />
          </>
        )}
      </FlexBox>
    </FlexBox>
  )
})

export const ImpressionLegend = connect((state: IRootState): IImpressionLegendProps => {
  const inventory = getInventory(state)
  return {
    viewType: inventory.mapViewType,
    mapImpressionsQuantiles: inventory.mapImpressionsQuantiles
  }
})(ImpressionLegendFn)
